import type { FunctionReturnType } from "convex/server"
import type { api, Settings } from "@/lib/api"
import { readStored, writeStored } from "@/lib/utils"

/**
 * What this browser keeps of the person signed in on it, so the app opens
 * with no connection after a reload: who they are (`users.me`) and their
 * settings (units, zone, week, rest), as last answered. The signed-in pages
 * render from it while the session cannot be checked; the server's answer
 * replaces it the moment there is one. Signing out forgets it, so a
 * shared browser keeps nobody's name.
 *
 * Read once per page and kept in memory, so the many readers of the
 * settings do not parse storage on every render.
 */

/** The signed-in person, as `users.me` returns them. */
export type Me = NonNullable<FunctionReturnType<typeof api.users.me>>

const ME_KEY = "trainy:me"
const settingsKey = (userId: string) => `trainy:settings:${userId}`

const loaded = new Map<string, unknown>()

function read<T>(key: string, valid: (value: unknown) => value is T): T | null {
  if (!loaded.has(key)) {
    let value: unknown = null
    try {
      value = JSON.parse(readStored(key) ?? "null")
    } catch {
      value = null
    }
    loaded.set(key, valid(value) ? value : null)
  }
  return (loaded.get(key) as T | null) ?? null
}

function write(key: string, value: unknown) {
  loaded.set(key, value)
  writeStored(key, value === null ? null : JSON.stringify(value))
}

function isMe(value: unknown): value is Me {
  if (typeof value !== "object" || value === null) return false
  const me = value as Record<string, unknown>
  return (
    typeof me.id === "string" &&
    typeof me.name === "string" &&
    typeof me.email === "string" &&
    (me.image === null || typeof me.image === "string")
  )
}

function isSettings(value: unknown): value is Settings {
  if (typeof value !== "object" || value === null) return false
  const settings = value as Record<string, unknown>
  return (
    (settings.unitSystem === "metric" || settings.unitSystem === "imperial") &&
    typeof settings.timeZone === "string" &&
    typeof settings.training === "object" &&
    settings.training !== null &&
    typeof settings.reports === "object" &&
    settings.reports !== null
  )
}

/** The person last signed in here, as the server last described them, or null. */
export function rememberedMe(): Me | null {
  return read(ME_KEY, isMe)
}

/** Keeps the signed-in person as the server describes them. Unchanged, nothing is written. */
export function rememberMe(me: Me) {
  const current = rememberedMe()
  if (current && JSON.stringify(current) === JSON.stringify(me)) return
  write(ME_KEY, me)
}

/** A person's settings as last answered here, or null. */
export function rememberedSettings(userId: string): Settings | null {
  return read(settingsKey(userId), isSettings)
}

/** Keeps a person's settings as the server answered them. Unchanged, nothing is written. */
export function rememberSettings(userId: string, settings: Settings) {
  const current = rememberedSettings(userId)
  if (current && JSON.stringify(current) === JSON.stringify(settings)) return
  write(settingsKey(userId), settings)
}

/** Forgets the person signed in here, and their settings. */
export function forgetAccount() {
  const me = rememberedMe()
  if (me) write(settingsKey(me.id), null)
  write(ME_KEY, null)
}
